/**
 * Dark mode for SVG files shown through <img>.
 *
 * CSS cannot reach inside an <img>, so a dark glyph (most of our icon files)
 * would vanish on a dark surface. Inverting every SVG fixes those but wrecks
 * everything else: illustrations turn negative and white-on-colour glyphs turn
 * black. So each image is looked at once and tagged `data-dark-invert` only when
 * it is a DARK MONOCHROME drawing; the stylesheet (components/theme.ts) inverts
 * tagged images in dark mode and leaves the rest alone.
 *
 * Small SVGs are inlined by the bundler as data URIs, which can be read directly.
 * Larger ones are separate files (illustrations, logos): those are fetched and
 * judged the same way, and simply stay untouched if the fetch is not allowed.
 * `data-keep-colors` on an <img> always opts it out.
 */

const INVERT_ATTRIBUTE = 'data-dark-invert'
const COLOR_PATTERN = /#[0-9a-fA-F]{6}\b|#[0-9a-fA-F]{3}\b|\b(?:black|white)\b/g

// src → should invert. `undefined` while a file is still being fetched.
const verdicts = new Map<string, boolean | undefined>()

const luminance = (literal: string): number => {
  const value = literal.toLowerCase()
  if (value === 'white') {
    return 1
  }
  if (value === 'black') {
    return 0
  }
  let hex = value.slice(1)
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((char) => char + char)
      .join('')
  }
  const number = parseInt(hex, 16)
  // eslint-disable-next-line no-bitwise
  const [r, g, b] = [(number >> 16) & 255, (number >> 8) & 255, number & 255]
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255
}

// Dark monochrome = every colour it names is dark (white cut-outs are allowed).
// No colour at all means the SVG default, black.
const isDarkMonochrome = (svg: string): boolean => {
  const colors = (svg.match(COLOR_PATTERN) || []).filter((color) => luminance(color) < 0.97)
  return colors.every((color) => luminance(color) < 0.5)
}

const decodeDataUri = (src: string): string | null => {
  const comma = src.indexOf(',')
  if (comma === -1) {
    return null
  }
  const payload = src.slice(comma + 1)
  try {
    return src.slice(0, comma).includes(';base64') ? atob(payload) : decodeURIComponent(payload)
  } catch {
    return null
  }
}

const tag = (img: HTMLImageElement, invert: boolean) => {
  if (invert) {
    img.setAttribute(INVERT_ATTRIBUTE, '')
  } else {
    img.removeAttribute(INVERT_ATTRIBUTE)
  }
}

const tagAllWithSrc = (src: string, invert: boolean) => {
  document.querySelectorAll('img').forEach((img) => {
    if (img.getAttribute('src') === src) {
      tag(img, invert)
    }
  })
}

const inspect = (img: HTMLImageElement) => {
  const src = img.getAttribute('src') || ''
  const isSvgData = src.startsWith('data:image/svg')
  const isSvgFile = /\.svg(\?|#|$)/i.test(src)
  if (!isSvgData && !isSvgFile) {
    img.removeAttribute(INVERT_ATTRIBUTE)
    return
  }
  if (verdicts.has(src)) {
    tag(img, verdicts.get(src) === true)
    return
  }
  if (isSvgData) {
    const svg = decodeDataUri(src)
    const invert = svg !== null && isDarkMonochrome(svg)
    verdicts.set(src, invert)
    tag(img, invert)
    return
  }
  verdicts.set(src, undefined)
  fetch(src)
    .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
    .then((svg) => {
      const invert = isDarkMonochrome(svg)
      verdicts.set(src, invert)
      tagAllWithSrc(src, invert)
    })
    .catch(() => verdicts.set(src, false))
}

let installed = false

export const installDarkImageTagger = () => {
  if (
    installed ||
    typeof document === 'undefined' ||
    typeof MutationObserver === 'undefined' ||
    document.documentElement.getAttribute('data-theme-scope') !== 'app'
  ) {
    return
  }
  installed = true
  document.querySelectorAll('img').forEach(inspect)
  // Runs before the next paint, so a new icon never shows dark first
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes') {
        if (mutation.target instanceof HTMLImageElement) {
          inspect(mutation.target)
        }
        return
      }
      mutation.addedNodes.forEach((node) => {
        if (node instanceof HTMLImageElement) {
          inspect(node)
        } else if (node instanceof Element) {
          node.querySelectorAll('img').forEach(inspect)
        }
      })
    })
  }).observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['src']
  })
}
